/**
 * Typography Selector Component
 * Allows user to choose font pairing styles
 */

import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Radio,
  FormControlLabel,
  RadioGroup
} from '@mui/material';

const TypographyOptions = [
  {
    id: 'professional-sans',
    name: 'Professional Sans',
    description: 'Clean and modern for business',
    headingFont: 'Inter, sans-serif',
    bodyFont: 'Inter, sans-serif',
    preview: {
      heading: 'Professional & Clean',
      body: 'Perfect for corporate, legal, and medical websites'
    }
  },
  {
    id: 'elegant-serif',
    name: 'Elegant Serif',
    description: 'Sophisticated and trustworthy',
    headingFont: 'Playfair Display, serif',
    bodyFont: 'Source Sans Pro, sans-serif',
    preview: {
      heading: 'Elegant & Refined',
      body: 'Ideal for law firms, consulting, and premium services'
    }
  },
  {
    id: 'modern-geometric',
    name: 'Modern Geometric',
    description: 'Bold and contemporary',
    headingFont: 'Montserrat, sans-serif',
    bodyFont: 'Open Sans, sans-serif',
    preview: {
      heading: 'Modern & Bold',
      body: 'Great for tech startups, creative agencies, and modern brands'
    }
  },
  {
    id: 'friendly-rounded',
    name: 'Friendly Rounded',
    description: 'Approachable and warm',
    headingFont: 'Nunito, sans-serif',
    bodyFont: 'Nunito, sans-serif',
    preview: {
      heading: 'Friendly & Welcoming',
      body: 'Perfect for healthcare, education, and service businesses'
    }
  },
  {
    id: 'classic-traditional',
    name: 'Classic Traditional',
    description: 'Timeless and authoritative',
    headingFont: 'Merriweather, serif',
    bodyFont: 'Lato, sans-serif',
    preview: {
      heading: 'Classic & Timeless',
      body: 'Best for traditional industries, established firms, and institutions'
    }
  }
];

const TypographySelector = ({ value, onChange }) => {
  const selectedTypography = value || 'professional-sans';

  return (
    <Box sx={{ my: 3 }}>
      <Typography variant="h6" gutterBottom fontWeight="bold">
        Choose Typography Style
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Select the font pairing that matches your brand personality
      </Typography>

      <RadioGroup
        value={selectedTypography}
        onChange={(e) => onChange(e.target.value)}
      >
        <Grid container spacing={2}>
          {TypographyOptions.map((option) => (
            <Grid item xs={12} md={6} key={option.id}>
              <Card
                sx={{
                  cursor: 'pointer',
                  border: selectedTypography === option.id ? '2px solid' : '1px solid',
                  borderColor: selectedTypography === option.id ? 'primary.main' : 'divider',
                  bgcolor: selectedTypography === option.id ? 'primary.50' : 'background.paper',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: 'primary.main',
                    boxShadow: 2
                  }
                }}
                onClick={() => onChange(option.id)}
              >
                <CardContent>
                  <Box display="flex" alignItems="flex-start" mb={2}>
                    <FormControlLabel
                      value={option.id}
                      control={<Radio />}
                      label=""
                      sx={{ m: 0, mr: 1 }}
                    />
                    <Box>
                      <Typography variant="h6" fontWeight="bold">
                        {option.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {option.description}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Typography Preview */}
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: 'background.paper',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: option.headingFont,
                        fontSize: 20,
                        fontWeight: 'bold',
                        mb: 1
                      }}
                    >
                      {option.preview.heading}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: option.bodyFont,
                        fontSize: 14,
                        color: 'text.secondary'
                      }}
                    >
                      {option.preview.body}
                    </Typography>
                  </Box>

                  {/* Font Details */}
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Headings: {option.headingFont.split(',')[0]}
                      {' • '}
                      Body: {option.bodyFont.split(',')[0]}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </RadioGroup>
    </Box>
  );
};

export default TypographySelector;
